<script>
import _ from 'lodash'
import { ContentLoader } from 'vue-content-loader'
import { isMobile } from 'mobile-device-detect'

import { LAB_REPORTS_BY_BIOMARKER } from '../graphql/queries/LabReportQueries'
import { BIOMARKER_BY_ID } from '../graphql/queries/BiomarkerQueries'
import { getId } from '../utils/generateURL'
import { getTimerange } from '../utils/charts'
import planDataMixin from '../mixins/myPlanData'

import PrintHeader from '../components/globals/print/PrintHeader'
import PrintButton from '../components/globals/print/PrintButton'
import TimelineItem from '../components/timeline/TimelineItem'
import ResultsTimelineChart from '../components/biomarker/ResultsChart.js'

export default {
  name: 'BiomarkerView',
  components: {
    TimelineItem,
    PrintHeader,
    PrintButton,
    ContentLoader,
    ResultsTimelineChart
  },
  mixins: [planDataMixin],
  data() {
    return {
      biomarkerId: getId(this.$route.params.id),
      isMobile
    }
  },
  apollo: {
    biomarker: {
      query: BIOMARKER_BY_ID,
      variables() {
        return { id: this.biomarkerId }
      },
      update: data => data.biomarkerById
    },
    labReports: {
      query: LAB_REPORTS_BY_BIOMARKER,
      variables() {
        return { biomarker: this.biomarkerId }
      },
      update: data => data.labReportsByBiomarker
    }
  },
  computed: {
    getTimerange() {
      if (!this.labReports) return []
      const results = this.labReports.results.map(x => x.date)
      return getTimerange(results)
    },
    getSummary() {
      return this.biomarker ? this.biomarker.summary : {}
    },
    getResults() {
      if (!this.labReports) return []
      return this.labReports.results.sort((a, b) => (a.date > b.date ? 1 : -1))
    },
    getLastReport() {
      if (!this.labReports || !this.biomarker) return null
      // ToDo: OPTIMIZE_QUERIES
      const report = _.last(this.getResults)
      const { date, lab, ...result } = report

      return {
        date,
        lab,
        results: [
          {
            ...result,
            ...this.biomarker
          }
        ]
      }
    },
    hideChart() {
      const { type } = this.biomarker.range
      return type === 'text' || type === 'link'
    }
  }
}
</script>

<template>
  <div class="biomarker-single-view section">
    <section>
      <b-message v-if="isSubscribed" aria-close-label="Close message"
        >Showing last report only. Subscribe to track results over
        time.</b-message
      >
    </section>
    <ContentLoader
      v-if="$apollo.loading"
      :height="150"
      :width="isMobile ? 200 : 400"
      :speed="3"
    >
      <rect x="0" y="0" rx="3" ry="3" width="155" height="12" />
      <rect x="0" y="20" rx="3" ry="3" width="350" height="4" />
      <rect x="0" y="30" rx="3" ry="3" width="380" height="4" />
      <rect x="0" y="40" rx="3" ry="3" width="201" height="4" />
    </ContentLoader>

    <PrintHeader
      v-if="!$apollo.loading && labReports"
      :reports="[labReports]"
    />

    <div v-if="!$apollo.loading" class="content content-separate">
      <h2 class="title is-2">
        {{ biomarker.name }}
        <PrintButton />
      </h2>
      <!-- Description -->
      <div>
        <p>{{ getSummary.description }}</p>
      </div>

      <br />

      <div v-if="getLastReport" class="latest-report">
        <TimelineItem :report="getLastReport" :actions="false" />
      </div>

      <div
        v-if="!hideChart && biomarker && labReports"
        class="biomarker-timeline-chart"
      >
        <ResultsTimelineChart
          :item="biomarker"
          :results="getResults"
          :timerange="getTimerange"
          :styles="{ position: 'relative', height: '100%' }"
        />
      </div>

      <!-- Cause -->
      <h3 v-if="getSummary.cause" class="title is-3">
        What can cause it to change?
      </h3>
      <div>
        <p>{{ getSummary.cause }}</p>
      </div>

      <!-- Influence -->
      <h3 v-if="getSummary.influence" class="title is-3">
        What factors within my control could influence it?
      </h3>
      <div>
        <p>{{ getSummary.influence }}</p>
      </div>
    </div>
  </div>
</template>

<style lang="sass">
.message
  margin-bottom: 2em
  .message-header
    font-weight: 400
.biomarker-timeline-chart
  margin-top: 1.5em
  height: 360px
  position: relative
</style>
